import { getPricesWithPromos } from '@services/promos.service';

import {
    OptionSouscrite,
    SapicConnectivity,
    SapicFundingType,
    SapicProductTypeV5,
    SapicResponseTypeV5,
} from '@app-types/api';
import { FaiProduct, FaiType } from '@app-types/fai';
import {
    Connectivity,
    DiscountEnumType,
    FundingType,
    ObligationType,
    OptionType,
    PhoneType,
    PlanType,
    Product,
    ProductTypeId,
    RepeaterProduct,
    SubscribedOption,
} from '@app-types/product';

import { ApiDiscountMapper } from './api-discount.mapper';

export class SapicMapper {
    static mapToProduct(sapicProduct: SapicProductTypeV5, isConnected: boolean): Product {
        const funding = sapicProduct.modesDeFinancement?.[0];

        const initialDeposit = funding?.propositionsCommerciales?.[0]?.apportInitial ?? 0;
        const sumODRDiscounts =
            sapicProduct.promotions?.reduce((sum, { reduction, type }) => {
                if (type !== DiscountEnumType.ODR) {
                    return sum;
                }
                return sum + reduction;
            }, 0) ?? 0;
        const priceWithOdr = initialDeposit - sumODRDiscounts;
        const price = priceWithOdr < 1 ? 1 : priceWithOdr;
        const discounts = sapicProduct.promotions?.map(ApiDiscountMapper.mapPromotionSapic) ?? [];
        const fundings = sapicProduct.modesDeFinancement?.map(SapicMapper.fundingMapper) ?? [];
        return {
            typeId: SapicMapper.mapSapicTypeToPlanType(sapicProduct.type),
            gencode: sapicProduct.gencode,
            url: sapicProduct.url,
            inStock: sapicProduct.estEnStock ?? false,
            name: sapicProduct.nom,
            categories: sapicProduct.categories,
            price,
            prices: getPricesWithPromos(
                {
                    initial: sapicProduct.prix.initial,
                    forever: sapicProduct.prix.pourToujours,
                    final: sapicProduct.prix.final,
                },
                discounts ?? [],
                isConnected,
            ),
            promoLabel: sapicProduct.libellePromo,
            discounts,
            products: [],
            fundings: fundings,
            small_image: sapicProduct.miniature,
            neededElements: sapicProduct.necessiteElements?.map((el) => ({
                alternatives: el.alternatives
                    .filter((alt) => alt.identifiant)
                    .map((alt) => ({
                        typeCatalogue: alt.typeCatalogue,
                        commercialComponentCategories: alt.categoriesComposantCommercial,
                        identifier: alt.identifiant,
                        label: alt.libelle,
                    })),
            })),
        };
    }

    static readonly mapToFai = (sapicProduct: SapicProductTypeV5): FaiType => {
        const discounts = sapicProduct.promotions?.map(ApiDiscountMapper.mapPromotionSapic) ?? [];
        const rentalFees = sapicProduct.fraisDeLocation ?? 0;
        const prices = getPricesWithPromos(
            {
                initial: sapicProduct.prix.initial + rentalFees,
                forever: sapicProduct.prix.pourToujours + rentalFees,
                final: sapicProduct.prix.final + rentalFees,
            },
            discounts,
            false,
        );
        return {
            price: sapicProduct.prix.initial + rentalFees,
            prices,
            sku: sapicProduct.gencode,
            speedDown: sapicProduct.debitDescendant,
            speedUp: sapicProduct.debitAscendant,
            obligation: sapicProduct.dureeEngagement,
            categoryType: sapicProduct.typeCategorieFai,
            discounts,
            range: sapicProduct.gamme ?? '',
            rangeNg: sapicProduct.gammeNg ?? '',
            technology: sapicProduct.technologie,
            neededElements: sapicProduct?.necessiteElements?.flatMap((el) =>
                el.alternatives
                    ?.filter((alt) => alt.identifiant)
                    ?.map((alt) => ({ gencode: alt.identifiant, label: alt.libelle })),
            ),
            play: sapicProduct.jeu ?? '3P',
            commercialSeries: sapicProduct.serieCommerciale ?? '',
            type: sapicProduct.type,
        };
    };

    static fundingMapper(funding: SapicFundingType): FundingType {
        const fundingData = funding.propositionsCommerciales[0]!;

        return {
            label: funding.libelle,
            type: funding.type,
            favorite: funding.prefere,
            initialDeposit: fundingData.apportInitial,
            amountToFinance: fundingData.montantAFinancer,
            totalFinancingCost: fundingData.coutTotalFinancement,
            APR: fundingData.tAEG,
            interestAmount: fundingData.montantInterets,
            fixedInterestRate: fundingData.tauxDebiteurFixe,
            installmentCount: fundingData.nbrEcheances,
            monthlyAmount: fundingData.montantMensualite,
        };
    }

    static mapToRepeater(
        apiData: SapicProductTypeV5,
        faiProducts?: FaiProduct[],
        isConnected = false,
    ): RepeaterProduct {
        const baseProduct = SapicMapper.mapToProduct(apiData, isConnected);
        const faiProduct = faiProducts?.find((fai) => fai.gencode_repeater === apiData.gencode);

        if (!faiProduct) {
            return baseProduct;
        }

        return {
            ...baseProduct,
            imgRepeater: faiProduct.img_repeater,
            name: faiProduct.title_repeater ?? baseProduct.name,
            shortDescription: faiProduct.short_description,
            longDescription: faiProduct.long_description,
            textLink: faiProduct.text_link,
        };
    }

    static mapToPlan(apiData: SapicProductTypeV5, isConnected: boolean): PlanType {
        const product = SapicMapper.mapToProduct(apiData, isConnected);
        const planData: Partial<PlanType> = {
            gencode: apiData.gencode,
            price: apiData.prix.initial,
            products: [],
            obligation: apiData?.dureeEngagement || ObligationType.NONE,
            obligationLabel: SapicMapper.getObligationLabel(apiData?.dureeEngagement),
            name: apiData.nom.replace('Avantages Smartphone', ''),
            dataEnvelope: apiData.libelleEnveloppeData || '',
            connectivity: SapicMapper.mapSapicConnectivityToConnectivity(apiData.connectivite),
            technology: apiData.technologie,
        };

        return { ...product, ...planData } as PlanType;
    }

    static mapToPhone(apiData: SapicProductTypeV5, isConnected: boolean): PhoneType {
        const product = SapicMapper.mapToProduct(apiData, isConnected);
        const phoneData: Partial<PhoneType> = {
            manufacturer: apiData.marque,
            etiquetteAnimCo: apiData.etiquetteAnimCo,
            colorCode: apiData.couleurs ? apiData.couleurs[0] : undefined,
            image: apiData.medias ? apiData.medias[0] : '',
        };

        return { ...product, ...phoneData } as PhoneType;
    }

    static mapToOption(apiData: SapicProductTypeV5): OptionType {
        return {
            gencode: apiData.gencode,
            name: apiData.nom,
            prices: {
                initial: apiData.prix.initial,
                forever: apiData.prix.pourToujours,
                final: apiData.prix.final,
            },
            image: apiData.medias ? apiData.medias[0] : undefined,
            pennantText: apiData.descriptionCourte,
            promoLabel: apiData.libellePromo,
            categories: apiData.categories,
            small_image: apiData.miniature,
        };
    }

    static mapToSubscribedOption(apiData: OptionSouscrite): SubscribedOption {
        return {
            optionId: apiData.idOption,
            label: apiData.libelle,
            phoneNumber: apiData.numeroLigne,
        };
    }

    static mapResponseToOptionsWithSubscribedOptions(response: SapicResponseTypeV5): {
        options: OptionType[];
        subscribedOptions: SubscribedOption[];
    } {
        const options = response.produits.map(SapicMapper.mapToOption);
        const subscribedOptions: SubscribedOption[] = Array.from(
            new Map(
                response.produits
                    .flatMap((product) => product.optionsSouscrites ?? [])
                    .map((option) => [
                        `${option.idOption ?? 'unknown'}-${option.numeroLigne ?? ''}-${option.libelle ?? ''}`,
                        SapicMapper.mapToSubscribedOption(option),
                    ]),
            ).values(),
        );
        return { options, subscribedOptions };
    }

    static getObligationLabel(obligation?: ObligationType) {
        switch (obligation) {
            case ObligationType.MONTHLY_24:
                return 'engagement 24 mois';
            case ObligationType.MONTHLY_12:
                return 'engagement 12 mois';
            default:
                return 'sans engagement';
        }
    }

    static mapSapicConnectivityToConnectivity(connectivity: SapicConnectivity | undefined): Connectivity | undefined {
        switch (connectivity) {
            case SapicConnectivity.FIVE_G:
                return Connectivity.FIVE_G;
            case SapicConnectivity.FOUR_G_PLUS:
                return Connectivity.FOUR_G_PLUS;
            default:
                return Connectivity.FOUR_G;
        }
    }

    static mapSapicTypeToPlanType(type: string): ProductTypeId {
        const typeValues = Object.values(ProductTypeId);
        const typeValue = typeValues.find((value) => value === type);
        return typeValue || ProductTypeId.DEFAULT;
    }
}
